import clsx from "clsx";
import { ChevronRight, CircleAlert, CircleCheck, Ellipsis, Info, Loader2, PencilLine, TriangleAlert } from "lucide-react";
import { createContext, type CSSProperties, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

/*
 * Small building blocks (see styles.css for the classes). Buttons and fields use the classes
 * there directly: btn-primary, btn-secondary, btn-positive, btn-danger, btn-icon, input.
 */

export function Spinner({ full, className }: Readonly<{ full?: boolean; className?: string }>) {
    const icon = <Loader2 className={clsx("size-5 animate-spin text-muted", className)} />;
    return full ? <div className="grid h-full place-items-center">{icon}</div> : icon;
}

/** Page title row: title (and a line under it) on the left, actions on the right. */
export function PageHeader({ title, subtitle, actions }: Readonly<{ title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }>) {
    return (
        <div className="page-header">
            <div className="min-w-0">
                <h1>{title}</h1>
                {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
    );
}

/** White panel with a grey header strip. `flush` drops the body padding (for list tables). */
export function Panel(props: Readonly<{ title?: ReactNode; actions?: ReactNode; flush?: boolean; className?: string; id?: string; children: ReactNode }>) {
    return (
        <section id={props.id} className={clsx("panel scroll-mt-4", props.className)}>
            {props.title || props.actions ? (
                <div className="panel-header">
                    {typeof props.title === "string" ? <h2>{props.title}</h2> : props.title}
                    {props.actions ? <div className="flex flex-wrap items-center gap-2">{props.actions}</div> : null}
                </div>
            ) : null}
            <div className={props.flush ? "overflow-x-auto" : "panel-body"}>{props.children}</div>
        </section>
    );
}

/**
 * A bordered group of related fields (or figures) in a window's tab, e.g. an item's name and its
 * tags. `shaded` for read-only summaries. Fields inside are spaced out; pass e.g. "space-y-2" to
 * tighten them.
 */
export function Box(props: Readonly<{ title?: ReactNode; shaded?: boolean; className?: string; children: ReactNode }>) {
    return (
        <section className={clsx("box", props.shaded && "is-shaded", props.className)}>
            {props.title ? <h3 className="box-title">{props.title}</h3> : null}
            {props.children}
        </section>
    );
}

export type Tone ="grey" | "green" | "blue" | "amber" | "red";

export function Badge({ tone = "grey", title, children }: Readonly<{ tone?: Tone; title?: string; children: ReactNode }>) {
    return (
        <span className={clsx("badge", tone !== "grey" && `is-${tone}`)} title={title}>
            {children}
        </span>
    );
}

/**
 * What needs doing about a value in a list, in its place: an icon and the value or a word or two
 * ("Missing"), or the icon alone beside something, with `title` saying why and what to do. The row
 * gets `is-warning` / `is-danger` (styles.css) to match, so every list flags things the same way.
 * Raised above a row's click area (rows open on a click anywhere), so its hover text shows.
 */
export function ListWarning({ tone = "warning", title, children }: Readonly<{ tone?: "warning" | "danger"; title: string; children?: ReactNode }>) {
    const Icon = tone === "danger" ? CircleAlert : TriangleAlert;
    return (
        <span
            className={clsx("relative z-1 inline-flex max-w-full min-w-0 items-center gap-1 align-middle font-medium", tone === "danger" ? "text-danger" : "text-warning")}
            title={title}
            role={children ? undefined : "img"}
            aria-label={children ? undefined : title}
        >
            <Icon className="size-3.5 shrink-0" />
            {children ? <span className="truncate">{children}</span> : null}
        </span>
    );
}

/** `value` 0–1, or null for an unknown total (a moving stripe). */
export function ProgressBar({ value, label, className }: Readonly<{ value: number | null; label: string; className?: string }>) {
    // Without a value attribute it's indeterminate (a moving stripe).
    const progress = value === null ? {} : { value: Math.min(1, Math.max(0, value)) };
    return <progress className={clsx("progress", className)} max={1} aria-label={label} {...progress} />;
}

/**
 * Text for a button whose label changes (Pause/Resume, Resync/"Resync all?"): it's always as wide
 * as the longest label, so the button doesn't change size and nothing around it moves.
 */
export function StableLabel({ labels, shown }: Readonly<{ labels: readonly string[]; shown: string }>) {
    return (
        <span className="stable-label">
            {labels.map((label) => (
                <span key={label} aria-hidden={label === shown ? undefined : true}>
                    {label}
                </span>
            ))}
        </span>
    );
}

const noticeIcons = { info: Info, error: CircleAlert, warning: TriangleAlert, success: CircleCheck };

/** Inside an Issues box: each Notice is a line of it. */
const InIssues = createContext(false);

/**
 * A record's problems as one box, "Needs attention": `chips` on its first line beside the heading (an
 * IssueChip each: a button to where it's put right), then each Notice inside a line of it (its icon,
 * message and buttons: one with nowhere else to be put right), in the order given (worst first).
 * Not shown when none of them is (they each decide), so it can hold every check a record has.
 */
export function Issues({ chips, children }: Readonly<{ chips?: ReactNode; children?: ReactNode }>) {
    return (
        <InIssues value={true}>
            <section className="issues hidden has-[.issue]:block has-[.issue-chip]:block" aria-label="Needs attention">
                <div className="issues-head">
                    <h3 className="issues-title">Needs attention</h3>
                    {chips}
                </div>
                {children}
            </section>
        </InIssues>
    );
}

/**
 * One problem on an Issues box's first line: what it is ("Price to update"), as a button to where it's
 * put right (`onShow`: its tab, scrolled to and flashed). Without one (nowhere to show it), just what it is;
 * `title`: more about it, on hover. `unsaved`: changes being made, not saved yet (green, the editing colour), not a problem.
 */
export function IssueChip({ tone = "warning", label, title, onShow }: Readonly<{ tone?: "warning" | "error" | "unsaved"; label: string; title?: string; onShow?: () => void }>) {
    const Icon = tone === "unsaved" ? PencilLine : noticeIcons[tone];
    const className = clsx("issue-chip", tone === "error" && "is-error", tone === "unsaved" && "is-unsaved");
    const body = (
        <>
            <Icon className="size-3.5 shrink-0" />
            {label}
            {onShow ? <ChevronRight className="size-3.5 shrink-0" /> : null}
        </>
    );
    if (!onShow) {
        return (
            <span className={className} title={title}>
                {body}
            </span>
        );
    }
    return (
        <button type="button" className={className} title={title ?? `Show: ${label}`} onClick={onShow}>
            {body}
        </button>
    );
}

/**
 * A section's heading above its box, like a field's label: its name on the left, a few words about it on the right (under it when
 * narrow). Name tags, Pricing, an item's Group.
 */
export function SectionHeading({ id, label, aside }: Readonly<{ id?: string; label: string; aside?: ReactNode }>) {
    return (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
            <h4 id={id} className="field-label">
                {label}
            </h4>
            {aside ? <p className="m-0 text-(length:--label-text) text-muted sm:text-right">{aside}</p> : null}
        </div>
    );
}

/**
 * A section of a record's tab, as its pricing, name tags and cost are: its heading above (SectionHeading), then a card: any
 * `header` along its top (a toolbar, its group), its body (`bodyClassName`: padded, unless it's a list edge to edge; nothing in
 * it: not there), then along its bottom its warnings (`fix`: FixHere attached) and anything else (`footer`: a save bar, an
 * add link). `tone`: an item overriding its group's, blue. Brought into view with its heading by a warning's Show.
 */
export function SectionCard(
    props: Readonly<{
        label: string;
        aside?: ReactNode;
        header?: ReactNode;
        tone?: "override";
        bodyClassName?: string;
        fix?: ReactNode;
        footer?: ReactNode;
        children?: ReactNode;
    }>,
) {
    const override = props.tone === "override";
    return (
        <div className="space-y-1" data-fix-scope>
            <SectionHeading label={props.label} aside={props.aside} />
            <div className={clsx("fix-card overflow-clip rounded-md border", override ? "border-override/30" : "border-line")}>
                {props.header}
                <div className={clsx(props.bodyClassName ?? "space-y-3.5 p-3", override && "bg-override-tint", "empty:hidden")}>{props.children}</div>
                {props.footer}
                {props.fix}
            </div>
        </div>
    );
}

/**
 * A draft's save bar, inside the edge its box gives it: what saving does on the left (`message`, then "Saving..." or `busy`'s
 * words while it does), its buttons on the right (under it only when narrow), and why it couldn't under them. An item's
 * pricing, a group's, name tags.
 */
export function DraftBar({ message, busy, actions, error }: Readonly<{ message: ReactNode; busy?: boolean | string | null; actions: ReactNode; error?: Error | null }>) {
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:flex-nowrap">
                {/* Saving: just how far it's got, in the message's place (on one line, nothing jumping about under it). */}
                <p className="m-0 min-w-0 flex-1 text-muted">
                    {busy ? (
                        <span className="inline-flex items-center gap-2">
                            <Spinner className="size-4" /> {typeof busy === "string" ? busy : "Saving..."}
                        </span>
                    ) : (
                        message
                    )}
                </p>
                <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
            </div>
            <ErrorNotice error={error ?? null} />
        </div>
    );
}

/**
 * A message strip. Errors are announced straight away (alert); the rest are <output> (a polite
 * status). `actions` (its buttons) go on the right of the same line, under it when narrow; the icon
 * stays beside the message's first line either way. Inside Issues, a line of that box instead.
 */
export function Notice({
    tone = "info",
    title,
    actions,
    children,
}: Readonly<{ tone?: keyof typeof noticeIcons; /** What it's about, in bold before the rest ("No item number."): the same in every warning. */ title?: ReactNode; actions?: ReactNode; children: ReactNode }>) {
    const Icon = noticeIcons[tone];
    const inIssues = useContext(InIssues);
    const body = title ? (
        <>
            <strong>{title}</strong> {children}
        </>
    ) : (
        children
    );
    if (inIssues) {
        return (
            <div className={clsx("issue", `is-${tone}`)} role={tone === "error" ? "alert" : undefined}>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    <Icon className="mt-px size-4 shrink-0" />
                    <div className="min-w-0 flex-1">{body}</div>
                </div>
                {actions ? <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div> : null}
            </div>
        );
    }
    const className = clsx("notice", tone !== "info" && `is-${tone}`, actions && "flex-wrap items-center justify-between gap-x-3 gap-y-1.5");
    const message = (
        <>
            <Icon className="mt-px size-4 shrink-0" />
            <div className="min-w-0 flex-1">{body}</div>
        </>
    );
    const content = actions ? (
        <>
            <div className="flex min-w-0 flex-1 items-start gap-2">{message}</div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
        </>
    ) : (
        message
    );
    if (tone === "error") {
        return (
            <div className={className} role="alert">
                {content}
            </div>
        );
    }
    return <output className={className}>{content}</output>;
}

/** Error from a query or mutation, if any. */
export function ErrorNotice({ error }: Readonly<{ error: Error | null | undefined }>) {
    return error ? <Notice tone="error">{error.message}</Notice> : null;
}

/**
 * A button that opens a small panel under it (or above it, near the bottom of the window). The
 * panel is placed against the window (not inside the page), so a scrolling table can't cut it
 * off. Closes on a click outside, Escape, scrolling or resizing. `children` gets `close`.
 */
export function Popover(
    props: Readonly<{
        /** What the button shows. */
        button: ReactNode;
        buttonClassName: string;
        /** The button's name for screen readers and on hover. */
        label: string;
        /** The panel's width (a Tailwind class). */
        width?: string;
        /**
         * Open beside the button, upwards from its foot, rather than under or over it: for a button at
         * the foot of a side column (a window's More), so the panel stays over the window. Without the
         * room beside it (a phone), as usual.
         */
        beside?: boolean;
        children: (close: () => void) => ReactNode;
    }>,
) {
    const button = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLDivElement>(null);
    const [place, setPlace] = useState<CSSProperties | null>(null);
    const close = useCallback(() => setPlace(null), []);

    useEffect(() => {
        if (!place) return;
        const onDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (!panel.current?.contains(target) && !button.current?.contains(target)) close();
        };
        // Escape closes just the panel (caught first, so a window it's in stays open).
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            close();
        };
        // The page (or a window) scrolling moves the button away: close. Its own list scrolling doesn't.
        const onScroll = (e: Event) => !panel.current?.contains(e.target as Node) && close();
        // Pointer presses (not mousedown): dragging a window (RecordWindow) cancels the mouse events, so it would be missed and the panel left behind.
        document.addEventListener("pointerdown", onDown);
        document.addEventListener("keydown", onKey, true);
        globalThis.addEventListener("resize", close);
        globalThis.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("pointerdown", onDown);
            document.removeEventListener("keydown", onKey, true);
            globalThis.removeEventListener("resize", close);
            globalThis.removeEventListener("scroll", onScroll, true);
        };
    }, [place, close]);

    const toggle = () => {
        const rect = button.current?.getBoundingClientRect();
        if (place || !rect) return close();
        if (props.beside && rect.right + 320 < globalThis.innerWidth) {
            setPlace({ left: rect.right + 6, bottom: globalThis.innerHeight - rect.bottom });
            return;
        }
        // Lined up with the button's far edge from the nearer side of the screen, so it opens inwards (e.g. from a sidebar).
        const across: CSSProperties = rect.left < globalThis.innerWidth / 2 ? { left: rect.left } : { right: globalThis.innerWidth - rect.right };
        const upwards = rect.bottom + 260 > globalThis.innerHeight;
        setPlace(upwards ? { ...across, bottom: globalThis.innerHeight - rect.top + 4 } : { ...across, top: rect.bottom + 4 });
    };

    return (
        <>
            <button ref={button} type="button" className={props.buttonClassName} aria-label={props.label} title={props.label} aria-expanded={Boolean(place)} onClick={toggle}>
                {props.button}
            </button>
            {place ? (
                // Its own text styles: it can sit inside a table header (small, grey, no wrapping). Clickable even
                // inside something that isn't (a label drawn over a field).
                <div ref={panel} className={clsx("card pointer-events-auto fixed z-50 p-1 text-left text-sm font-normal whitespace-normal text-ink", props.width ?? "w-72")} style={place}>
                    {props.children(close)}
                </div>
            ) : null}
        </>
    );
}

/** A "⋯" button that opens a small menu of actions (MenuItem). `children` gets `close`. */
export function MoreMenu({ label, children }: Readonly<{ label: string; children: (close: () => void) => ReactNode }>) {
    return (
        <Popover label={label} buttonClassName="btn-icon" button={<Ellipsis className="size-4" />}>
            {children}
        </Popover>
    );
}

/** One action in a MoreMenu: a name, and a line saying what it does. */
export function MenuItem(
    props: Readonly<{ icon: ReactNode; label: string; help: string; danger?: boolean; disabled?: boolean; onClick: () => void }>,
) {
    return (
        <button
            type="button"
            className="flex w-full cursor-pointer items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-2 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
            disabled={props.disabled}
            onClick={props.onClick}
        >
            <span className={clsx("mt-0.5", props.danger ? "text-danger" : "text-muted")}>{props.icon}</span>
            <span className="min-w-0">
                <span className={clsx("block font-medium", props.danger && "text-danger")}>{props.label}</span>
                <span className="block text-xs text-muted">{props.help}</span>
            </span>
        </button>
    );
}

/** A small icon-only button in a list row (move, remove): `label` is its hover text and name. */
export function IconButton(props: Readonly<{ label: string; disabled?: boolean; onClick: () => void; children: ReactNode }>) {
    return (
        <button
            type="button"
            title={props.label}
            aria-label={props.label}
            disabled={props.disabled}
            onClick={props.onClick}
            className="rounded p-1 text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-30"
        >
            {props.children}
        </button>
    );
}

